import { useLocation, Link, useSearchParams } from 'react-router-dom';
import ExpandablePanel from '../../components/ExpandablePanel/ExpandablePanel';
import FilterChips from '../../components/FilterChips/FilterChips';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCourts } from '../../services/courtServices';
import {
  exportCasesExcel,
  getCreateInfo,
  getPagedCasesByUser,
} from '../../services/caseServices';
import CardStats from '../../components/CardStats/CardStats';
import DatePickerEs from '../../components/DatePickerEs/DatePickerEs';
import { getSponsors, getUsersPaged } from '../../services/usersServices';
import NewCaseModal from './NewCaseModal';
import { getClientsPaged } from '../../services/clientService';
import { getCurrentUser } from '../../services/authService';
import './legalcase.scss';
import { persistStateToUrl } from 'utils/urlState';
import { parseLocalDate } from 'utils/formatDate';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { legalCasesI18n } from './i18n';
import usePermissionsCauses from 'permissions/modules/causes/usePermissionsCauses';

const LegalCases = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const legalCasesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: legalCasesI18n });

  const {
    Card,
    IconButton,
    Input,
    SvgIcon,
    Dropdown,
    Button,
    Table,
    ComboBox,
    Badge,
  } = DesignSystemComponents;

  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const [dateRange, setDateRange] = useState(null);
  const [userId, setUserId] = useState(null);
  const [filters, setFilters] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activesCount, setActivesCount] = useState(0);
  const [finishedCount, setFinishedCount] = useState(0);
  const [legalCasesTablePage, setLegalCasesTablePage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [legalCases, setLegalCases] = useState([]);
  const [searchLegalCases, setSearchLegalCases] = useState('');
  const [lawyers, setLawyers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [selectedLawyersMulti, setSelectedLawyersMulti] = useState([]);
  const [clients, setClients] = useState([]);
  const [createInfo, setCreateInfo] = useState([]);
  const [businessAreaOptions, setBusinessAreaOptions] = useState([]);
  const [selectedGroupsMulti, setSelectedGroupsMulti] = useState([]);
  const myCasesOptions = useMemo(
    () => [
      { label: t('legalCases_myCases'), value: 'mis causas' },
      { label: t('legalCases_allFemale'), value: 'todos' },
    ],
    [t]
  );
  const [myCases, setMyCases] = useState(myCasesOptions[0]);
  const defaultCourt = useMemo(
    () => ({ label: t('legalCases_allCourts'), value: 0 }),
    [t]
  );
  const [courts, setCourts] = useState([]);
  const courtOptions = useMemo(
    () => [defaultCourt, ...courts],
    [defaultCourt, courts]
  );
  const [selectedCourts, setSelectedCourts] = useState(defaultCourt);
  const [selectedCourtsMulti, setSelectedCourtsMulti] = useState([]);
  const processOptions = useMemo(
    () => [
      { label: t('legalCases_process_resolution'), value: 'Resoluci\u00f3n' },
      { label: t('legalCases_process_appeal'), value: 'Apelaci\u00f3n' },
      { label: t('legalCases_process_writ'), value: 'Escrito' },
      {
        label: t('legalCases_process_receiverAction'),
        value: 'Actuaci\u00f3n Receptor',
      },
      {
        label: t('legalCases_process_notification'),
        value: 'Notificaci\u00f3n',
      },
    ],
    [t]
  );
  const stateOptions = useMemo(
    () => [
      { label: t('legalCases_state_active'), value: 1 },
      { label: t('legalCases_state_inactive'), value: 3 },
    ],
    [t]
  );
  const [selectedProcessOptions, setSelectedProcessOptions] = useState([]);
  const [selectedStateOptions, setSelectedStateOptions] = useState([]);
  const [fromMonitoring, setFromMonitoring] = useState(false);
  const [monitoringId, setMonitoringId] = useState(null);
  const [preselectedClient, setPreselectedClient] = useState(null);
  const [preselectedRol, setPreselectedRol] = useState(null);
  const activeCases = useMemo(
    () => [
      { label: t('legalCases_allFemale'), value: null },
      { label: t('legalCases_yes'), value: true },
      { label: t('legalCases_no'), value: false },
    ],
    [t]
  );
  const [selectedActiveCases, setSelectedActiveCases] = useState(
    activeCases[0]
  );
  const [monitoringIdentifier, setMonitoringIdentifier] = useState(null);

  const {
    canCreate,
    canDownloadExcel,
    canViewDetailSection,
    canViewBlockedClientCause,
  } = usePermissionsCauses();

  useEffect(() => {
    legalCasesI18n.changeLanguage(legalCasesLanguage);
  }, [legalCasesLanguage]);

  useEffect(() => {
    setMyCases(
      (prev) =>
        myCasesOptions.find((option) => option.value === prev?.value) ||
        myCasesOptions[0]
    );
  }, [myCasesOptions]);

  useEffect(() => {
    if (!filters) return;

    const selected = myCasesOptions.find(
      (opt) => opt.value === filters.MyCases
    );
    setMyCases(selected ?? myCasesOptions[0]);
  }, [filters?.MyCases, myCasesOptions]);

  useEffect(() => {
    if (!filters) return;
    setSearchLegalCases(filters.SearchLegalCases ?? '');
  }, [filters?.SearchLegalCases]);

  useEffect(() => {
    if (!filters || !courtOptions.length) return;

    // Single
    const single = courtOptions.find((c) => c.value === filters.Courts?.[0]);
    setSelectedCourts(single ?? defaultCourt);

    // Multi
    // const multi = courts.filter((c) => filters.Courts?.includes(c.value));
    // setSelectedCourtsMulti(multi);
  }, [filters?.Courts, courtOptions, defaultCourt]);

  useEffect(() => {
    if (!filters || !businessAreaOptions.length) return;

    const selected = businessAreaOptions.filter((g) =>
      filters.Groups?.includes(g.value)
    );

    setSelectedGroupsMulti(selected);
  }, [filters?.Groups, businessAreaOptions]);

  useEffect(() => {
    if (!filters) return;

    const selected = processOptions.filter((p) =>
      filters.Process?.includes(p.value)
    );

    setSelectedProcessOptions(selected);
  }, [filters?.Process, processOptions]);

  useEffect(() => {
    if (!filters) return;

    const selected = activeCases.find((opt) => opt.value === filters.IsActive);

    setSelectedActiveCases(selected ?? activeCases[0]);
  }, [filters?.IsActive, activeCases]);

  useEffect(() => {
    if (!filters?.Lawyers?.length) {
      setSelectedLawyersMulti([]);
      return;
    }

    if (!lawyers.length) return;

    const resolved = lawyers.filter((l) => filters.Lawyers.includes(l.id));

    setSelectedLawyersMulti(resolved);
  }, [filters?.Lawyers, lawyers]);

  const parseArray = (value) => (value ? value.split(',') : []);

  useEffect(() => {
    if (!userId) return;

    const restoredFilters = {
      UserId: userId,
      SearchLegalCases: searchParams.get('SearchLegalCases') ?? '',
      MyCases: searchParams.get('MyCases') ?? 'mis causas',
      Courts: parseArray(searchParams.get('Courts')).map(Number),
      StartDate: searchParams.get('StartDate'),
      EndDate: searchParams.get('EndDate'),
      IsActive:
        searchParams.get('IsActive') === null
          ? null
          : searchParams.get('IsActive') === 'true',
      Role: searchParams.get('Role') ?? '',
      Case: searchParams.get('Case') ?? '',
      Client: searchParams.get('Client') ?? '',
      Process: parseArray(searchParams.get('Process')),
      States: parseArray(searchParams.get('States')).map(Number),
      Lawyers: parseArray(searchParams.get('Lawyers')).map(Number),
      Groups: parseArray(searchParams.get('Groups')).map(Number),
    };

    setFilters(restoredFilters);

    const restoredSortKey = searchParams.get('sortKey');
    const restoredSortDir = searchParams.get('sortDir') ?? 'desc';
    const restoredPage = Number(searchParams.get('page') ?? 0);
    const restoredPageSize = Number(searchParams.get('pageSize') ?? 10);

    if (restoredSortKey) setSortKey(restoredSortKey);
    setSortDirection(restoredSortDir);
    setLegalCasesTablePage(restoredPage);
    setPageSize(restoredPageSize);
  }, [userId]);

  useEffect(() => {
    if (!filters?.StartDate && !filters?.EndDate) {
      setDateRange(null);
      return;
    }

    setDateRange({
      startDate: parseLocalDate(filters.StartDate),
      endDate: parseLocalDate(filters.EndDate),
    });
  }, [filters?.StartDate, filters?.EndDate]);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      persistStateToUrl({
        filters: next,
        sortKey,
        sortDirection,
        page: legalCasesTablePage,
        pageSize,
        setSearchParams,
      });

      return next;
    });
  };

  const handleSearchLegalCases = (e) => {
    setLegalCasesTablePage(0);
    setSearchLegalCases(e.target.value);
    handleFilterChange('SearchLegalCases', e.target.value);
  };

  const handleMyCases = (selected) => {
    setMyCases(selected);
    handleFilterChange('MyCases', selected.value);
  };

  const syncCourtsFilter = (singleCourt, multiCourts) => {
    const ids = [
      ...(singleCourt && singleCourt.value !== 0 ? [singleCourt.value] : []),
      ...(Array.isArray(multiCourts) ? multiCourts.map((c) => c.value) : []),
    ];

    const uniqueIds = [...new Set(ids)];

    handleFilterChange('Courts', uniqueIds);
  };

  const handleCourtsFilter = (selected) => {
    const value = selected ?? defaultCourt;
    setSelectedCourts(value);

    syncCourtsFilter(value, selectedCourtsMulti);
  };

  const handleLawyersMultiSelect = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return;

    setSelectedLawyersMulti(selectedItems);

    const selectedValues = selectedItems.map((item) => item.id);

    setFilters((prev) => {
      const next = {
        ...prev,
        Lawyers: selectedValues,
      };

      persistStateToUrl({
        filters: next,
        sortKey,
        sortDirection,
        page: legalCasesTablePage,
        pageSize,
        setSearchParams,
      });

      return next;
    });
  };

  const handleGroupsMultiSelect = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return;

    setSelectedGroupsMulti(selectedItems);

    const selectedValues = selectedItems.map((item) => item.value);

    setFilters((prev) => {
      const next = {
        ...prev,
        Groups: selectedValues,
      };

      persistStateToUrl({
        filters: next,
        sortKey,
        sortDirection,
        page: legalCasesTablePage,
        pageSize,
        setSearchParams,
      });

      return next;
    });
  };

  const handleActiveCases = (selected) => {
    setSelectedActiveCases(selected);

    let valueToSend = null;

    if (selected.value === true) {
      valueToSend = true;
    } else if (selected.value === false) {
      valueToSend = false;
    }

    handleFilterChange('IsActive', valueToSend);
  };

  const buildFilterChips = () => {
    if (!filters) return [];

    const chips = [];

    if (filters.SearchLegalCases) {
      chips.push({
        id: 'search',
        label: t('legalCases_chip_search'),
        value: filters.SearchLegalCases,
      });
    }

    if (filters.MyCases && filters.MyCases !== 'mis causas') {
      chips.push({
        id: 'myCases',
        label: t('legalCases_chip_cases'),
        value: myCases?.label || filters.MyCases,
      });
    }

    if (
      selectedCourts &&
      selectedCourts.value !== undefined &&
      selectedCourts.value !== 0
    ) {
      chips.push({
        id: 'courts',
        label: t('legalCases_chip_court'),
        value: selectedCourts.label,
      });
    }

    // Date range
    if (filters.StartDate || filters.EndDate) {
      const { StartDate, EndDate } = filters;
      let value = '';
      if (StartDate && EndDate && StartDate !== EndDate) {
        value = `${StartDate} - ${EndDate}`;
      } else {
        value = StartDate || EndDate;
      }

      chips.push({
        id: 'date',
        label: t('legalCases_dateFilter'),
        value,
      });
    }

    if (filters.Role) {
      chips.push({
        id: 'role',
        label: t('legalCases_roleFilter'),
        value: filters.Role,
      });
    }

    if (filters.Case) {
      chips.push({
        id: 'case',
        label: t('legalCases_caseFilter'),
        value: filters.Case,
      });
    }

    if (filters.Client) {
      chips.push({
        id: 'client',
        label: t('legalCases_clientFilter'),
        value: filters.Client,
      });
    }

    if (filters.Process && filters.Process.length > 0) {
      chips.push({
        id: 'process',
        label: t('legalCases_processFilter'),
        value: filters.Process.join(', '),
      });
    }

    if (filters.States && filters.States.length > 0) {
      const activeStates = stateOptions.filter((s) =>
        filters.States.includes(s.value)
      );
      const value = activeStates.length
        ? activeStates.map((s) => s.label).join(', ')
        : filters.States.join(', ');

      chips.push({
        id: 'states',
        label: t('legalCases_stateFilter'),
        value,
      });
    }

    if (filters.Lawyers && filters.Lawyers.length > 0) {
      const activeLawyers = selectedLawyersMulti.length
        ? selectedLawyersMulti.map((l) => l.label).join(', ')
        : `${filters.Lawyers.length} abogado(s)`;

      chips.push({
        id: 'lawyers',
        label: t('legalCases_lawyerFilter'),
        value: activeLawyers,
      });
    }

    if (filters.Groups && filters.Groups.length > 0) {
      const activeGroups = selectedGroupsMulti.length
        ? selectedGroupsMulti.map((g) => g.label).join(', ')
        : `${filters.Groups.length} grupo(s)`;

      chips.push({
        id: 'groups',
        label: t('legalCases_groupFilter'),
        value: activeGroups,
      });
    }

    return chips;
  };

  const handleRemoveFilterChip = (id) => {
    if (!filters) return;

    switch (id) {
      case 'search': {
        setSearchLegalCases('');
        handleFilterChange('SearchLegalCases', '');
        break;
      }
      case 'myCases': {
        const defaultMyCases = myCasesOptions[0];
        setMyCases(defaultMyCases);
        handleFilterChange('MyCases', defaultMyCases.value);
        break;
      }
      case 'courts': {
        setSelectedCourts(defaultCourt);
        handleFilterChange('Courts', []);
        break;
      }
      case 'date': {
        handleFilterChange('StartDate', null);
        handleFilterChange('EndDate', null);
        break;
      }
      case 'role': {
        handleFilterChange('Role', '');
        break;
      }
      case 'case': {
        handleFilterChange('Case', '');
        break;
      }
      case 'client': {
        handleFilterChange('Client', '');
        break;
      }
      case 'process': {
        setSelectedProcessOptions([]);
        handleFilterChange('Process', []);
        break;
      }
      case 'states': {
        setSelectedStateOptions([]);
        handleFilterChange('States', []);
        break;
      }
      case 'lawyers': {
        setSelectedLawyersMulti([]);
        handleFilterChange('Lawyers', []);
        break;
      }
      case 'groups': {
        setSelectedGroupsMulti([]);
        handleFilterChange('Groups', []);
        break;
      }
      default:
        break;
    }
  };

  const handleClearAllFilterChips = () => {
    // Clear advanced filters
    handleClearFilters();

    // Clear header-level filters
    setSearchLegalCases('');
    handleFilterChange('SearchLegalCases', '');

    const defaultMyCases = myCasesOptions[0];
    setMyCases(defaultMyCases);
    handleFilterChange('MyCases', defaultMyCases.value);

    setSelectedCourts(defaultCourt);
    handleFilterChange('Courts', []);

    setSelectedGroupsMulti([]);
    handleFilterChange('Groups', []);
  };

  const handleApplyFilters = () => {
    handleFilterChange('SearchLegalCases', '');
    setLegalCases([]);
  };

  const handleClearFilters = () => {
    handleFilterChange('StartDate', null);
    handleFilterChange('EndDate', null);
    handleFilterChange('Role', '');
    handleFilterChange('Case', '');
    handleFilterChange('Client', '');
    handleFilterChange('Courts', []);
    handleFilterChange('Process', []);
    handleFilterChange('States', []);
    handleFilterChange('Lawyers', []);
    handleFilterChange('IsActive', null);
    setSelectedActiveCases(activeCases[0]);
    setSelectedLawyersMulti([]);
    setSelectedProcessOptions([]);
    setSelectedStateOptions([]);
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportCasesExcel({
        ...filters,
        sortKey: sortKey,
        sortDirection: sortDirection,
      });
    } catch (error) {
      console.error('Error descargando Excel:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const fetchCourts = async () => {
    try {
      const resp = await getAllCourts();
      setCourts(resp);
    } catch (error) {
      console.error('Error al obtener tribunales: ', error);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      const resp = await getUsersPaged(1, 2000, true, search);

      const mapped = resp.users.map((u) => ({
        label: u.name,
        value: u.id,
        ...u,
      }));

      setLawyers(mapped);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
    }
  };

  const fetchSponsors = async () => {
    try {
      const resp = await getSponsors();

      const mapped = resp.map((u) => ({
        label: u.name,
        value: u.id,
        ...u,
      }));

      setSponsors(mapped);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
    }
  };

  const fetchLegalCases = async () => {
    if (!filters) return;
    try {
      setLoading(true);
      const resp = await getPagedCasesByUser(
        legalCasesTablePage + 1,
        pageSize,
        filters,
        sortKey,
        sortDirection
      );
      setLegalCases(resp.cases);
      setTotalCount(resp.total);
      setActivesCount(resp.activeCount);
      setFinishedCount(resp.closedCount);
    } catch (error) {
      setLegalCases([]);
      setTotalCount(0);
      setActivesCount(0);
      setFinishedCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async (q = '') => {
    try {
      const params = {
        page: 1,
        pageSize: 10,
        q,
        state: 'active',
      };

      const resp = await getClientsPaged(params);

      setClients(resp.items);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
    }
  };

  const fetchBusinessArea = async () => {
    try {
      const resp = await getCreateInfo();
      setCreateInfo(resp);

      const mapped = resp.businessArea
        .filter((u) => u.id !== 2 && u.id !== 11)
        .map((u) => ({
          label: u.name,
          value: u.id,
          ...u,
        }));

      setBusinessAreaOptions(mapped);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!userId) return;

    // Si hay parámetros en la URL, NO inicializar
    if ([...searchParams.keys()].length > 0) return;

    setFilters({
      UserId: userId,
      SearchLegalCases: null,
      MyCases: 'mis causas',
      Courts: [],
      StartDate: null,
      EndDate: null,
      IsActive: null,
      Role: '',
      Case: '',
      Client: '',
      Process: [],
      States: [],
      Lawyers: [],
      Groups: [],
    });
  }, [userId]);

  useEffect(() => {
    if (location.state?.fromMonitoring && location.state?.identifier) {
      setFromMonitoring(true);
      setMonitoringId(location.state.monitoringId);
      setMonitoringIdentifier(location.state.identifier);
      setPreselectedRol(location.state?.rol ?? null);
    }
  }, [location.state]);

  useEffect(() => {
    if (!filters || !userId) return;

    const fetchData = async () => {
      await fetchCourts();
      await fetchUsers();
      await fetchSponsors();
      await fetchClients();
      await fetchBusinessArea();
    };

    fetchData();

    const timeout = setTimeout(() => {
      fetchLegalCases();
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, legalCasesTablePage, pageSize, userId]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setLegalCasesTablePage(0);
    persistStateToUrl({
      filters,
      sortKey,
      sortDirection,
      page: 0,
      pageSize: newSize,
      setSearchParams,
    });
  };

  useEffect(() => {
    if (!userId) return;
    fetchLegalCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sortKey, sortDirection]);

  function getCaseStateBadge(status) {
    const normalized = String(status ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'activa') {
      return (
        <Badge text={t('legalCases_state_active')} color='success' size='sm' />
      );
    }
    if (normalized === 'inactiva') {
      return (
        <Badge text={t('legalCases_state_inactive')} color='error' size='sm' />
      );
    }
    return (
      <Badge text={status || t('legalCases_noState')} color='error' size='sm' />
    );
  }

  useEffect(() => {
    if (!fromMonitoring || !monitoringIdentifier) return;

    const fetchClientByIdentifier = async () => {
      try {
        const resp = await getClientsPaged({
          page: 1,
          pageSize: 1,
          rut: monitoringIdentifier,
          state: 'active',
        });

        if (resp.items?.length) {
          const client = resp.items[0];

          const resolved = {
            label: `${client.name} (${client.identifier})`,
            value: client.id,
            identifier: client.identifier,
          };

          setClients([client]);
          setPreselectedClient(resolved);
          setShowCaseModal(true);
        } else {
          console.warn('No se encontró cliente con rut:', monitoringIdentifier);
        }
      } catch (e) {
        console.error('Error resolviendo cliente por rut:', e);
      }
    };

    fetchClientByIdentifier();
  }, [fromMonitoring, monitoringIdentifier]);

  return (
    <div>
      {/* HEADER */}
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className='fw-semibold'>{t('legalCases_title')}</h2>
          <h4 className='fw-normal'>{t('legalCases_subtitle')}</h4>
        </div>
        <Button
          label={t('legalCases_newCaseButton')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => setShowCaseModal(true)}
          disabled={!canCreate}
        />
      </div>

      {/* STATS */}
      <div className='row mb-4'>
        <div className='col-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('legalCases_actives')}
            count={activesCount}
            subtitle={t('legalCases_inProgress')}
            icon={'briefcase'}
            loading={loading}
          />
        </div>
        <div className='col-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('legalCases_closed')}
            count={finishedCount}
            subtitle={t('legalCases_finished')}
            icon={'briefcase'}
            loading={loading}
          />
        </div>
      </div>

      {/* SEARCH & FILTER BAR */}
      <Card specialPadding='p-4'>
        <div className='row'>
          <div className='d-flex justify-content-end pb-3'>
            <Button
              iconStart='download'
              label={t('legalCases_downloadExcel')}
              variant='naked'
              className='mt-2'
              size='sm'
              onClick={handleExportExcel}
              disabled={isExportingExcel || !canDownloadExcel}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-3'>
            <Input
              icon='search'
              name='searchLegalCases'
              onChange={handleSearchLegalCases}
              placeholder={t('legalCases_searchPlaceholder')}
              type='text'
              value={searchLegalCases}
            />
          </div>
          <div className='col-2'>
            <Dropdown
              options={myCasesOptions}
              value={myCases}
              onChange={handleMyCases}
            />
          </div>
          <div className='col-3'>
            <ComboBox
              placeholder={t('legalCases_groupFilterPlaceholder')}
              options={businessAreaOptions}
              value={selectedGroupsMulti}
              onChange={handleGroupsMultiSelect}
              multiSelect
            />
          </div>
          <div className='col-3'>
            <ComboBox
              placeholder={t('legalCases_selectCourtPlaceholder')}
              options={courtOptions}
              onChange={handleCourtsFilter}
              value={selectedCourts}
            />
          </div>
          <IconButton
            ariaLabel={t('legalCases_filterAriaLabel')}
            icon='funnel'
            onClick={() => setShowPanel((prev) => !prev)}
            className='mt-1'
            size='lg'
          />
        </div>
        {!showPanel && (
          <FilterChips
            filters={buildFilterChips()}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilterChips}
          />
        )}

        {/* FILTER PANEL */}
        {showPanel && (
          <ExpandablePanel
            open={showPanel}
            showActions
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onClose={() => setShowPanel(false)}
            applyLabel={t('legalCases_applyFiltersButton')}
            clearLabel={t('legalCases_clearFiltersButton')}
            cancelLabel={t('legalCases_closeFilterButton')}
          >
            <div className='d-flex justify-content-between mb-3'>
              <div>
                <h4 className='fw-semibold '>{t('legalCases_filterTitle')}</h4>
                <p className='mini font-light fw-normal'>
                  {t('legalCases_filterSubtitle')}
                </p>
              </div>
              <IconButton
                ariaLabel={t('legalCases_closePanelAriaLabel')}
                icon='close'
                size='md'
                onClick={() => setShowPanel(false)}
              />
            </div>

            {/* FILTER ROW 1 */}
            <div className='row'>
              <div className='col-3'>
                <Input
                  label={t('legalCases_roleFilter')}
                  type='text'
                  onChange={(e) => handleFilterChange('Role', e.target.value)}
                  placeholder={t('legalCases_rolePlaceholder')}
                  value={filters.Role}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('legalCases_caseFilter')}
                  type='text'
                  onChange={(e) => handleFilterChange('Case', e.target.value)}
                  placeholder={t('legalCases_casePlaceholder')}
                  value={filters.Case}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('legalCases_clientFilter')}
                  type='text'
                  onChange={(e) => handleFilterChange('Client', e.target.value)}
                  placeholder={t('legalCases_clientPlaceholder')}
                  value={filters.Client}
                />
              </div>
              {/* Date */}
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <p className='caption mb-1'>{t('legalCases_dateFilter')}</p>
                  <DatePickerEs
                    initialRange={dateRange}
                    onChange={(range) => {
                      setDateRange(range);

                      const normalizeDate = (date) =>
                        date
                          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                          : null;

                      handleFilterChange(
                        'StartDate',
                        normalizeDate(range?.startDate)
                      );
                      handleFilterChange(
                        'EndDate',
                        normalizeDate(range?.endDate)
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            {/* FILTER ROW 2 */}
            <div className='row pt-3'>
              {/* States */}
              <div className='col-3'>
                <Dropdown
                  label={t('legalCases_activeFilter')}
                  options={activeCases}
                  value={selectedActiveCases}
                  onChange={handleActiveCases}
                />
              </div>
              {/* Lawyers */}
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('legalCases_lawyerFilter')}
                    placeholder={t('legalCases_selectLawyersPlaceholder')}
                    options={lawyers}
                    value={selectedLawyersMulti}
                    onChange={handleLawyersMultiSelect}
                    onInputChange={(text) => fetchUsers(text)}
                    multiSelect
                  />
                </div>
              </div>
            </div>
          </ExpandablePanel>
        )}

        {/* TABLE */}
        <div className='table-container'>
          <Table
            columns={[
              {
                accessor: 'code',
                label: t('legalCases_roleColumn'),
                sort: true,
              },
              {
                accessor: 'title',
                label: t('legalCases_caseColumn'),
                sort: true,
              },
              {
                accessor: 'court',
                label: t('legalCases_courtColumn'),
                sort: true,
              },
              {
                accessor: 'client',
                label: t('legalCases_clientColumn'),
                sort: true,
                render: (info) => (
                  <div className='client-double'>
                    <span className='ellipsis'>{`${info.client.name}`}</span>{' '}
                    <span className='mini'>{`${info.client.identifier}`}</span>
                  </div>
                ),
              },
              {
                accessor: 'businessArea',
                label: t('legalCases_businessAreaColumn'),
                sort: true,
              },
              {
                accessor: 'internalStateStatus',
                label: t('legalCases_stateColumn'),
                sort: true,
                render: (row) => (
                  <div className='d-flex align-items-center'>
                    {getCaseStateBadge(row.internalStateStatus)}

                    {row.hasHighPriority && (
                      <SvgIcon
                        name='exclamationTriangle'
                        size='1rem'
                        className='ms-2'
                        fill='var(--error-500)'
                      />
                    )}
                  </div>
                ),
              },
              {
                accessor: 'state',
                label: t('legalCases_actionsColumn'),
                render: (info) => (
                  <div className='d-flex'>
                    {canViewDetailSection ? (
                      <Link
                        to={`/detalle-causa/${info.id}/movimientos`}
                        state={{
                          caseData: info,
                          lawyers,
                          courts: courtOptions,
                        }}
                        className='text-decoration-none'
                      >
                        <IconButton
                          ariaLabel={t('legalCases_viewAriaLabel')}
                          icon='eye'
                          size='md'
                        />
                      </Link>
                    ) : (
                      <IconButton
                        ariaLabel={t('legalCases_viewAriaLabel')}
                        icon='eye'
                        size='md'
                        disabled
                      />
                    )}
                  </div>
                ),
              },
            ]}
            data={legalCases}
            page={legalCasesTablePage}
            onPageChange={(page) => {
              setLegalCasesTablePage(page);
              persistStateToUrl({
                filters,
                sortKey,
                sortDirection,
                page,
                pageSize,
                setSearchParams,
              });
            }}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            paginationMode='server'
            totalItems={totalCount}
            loading={loading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={({ sortKey: key, direction }) => {
              setSortKey(key);
              setSortDirection(direction);
              setLegalCasesTablePage(0);

              persistStateToUrl({
                filters,
                sortKey: key,
                sortDirection: direction,
                page: 0,
                pageSize,
                setSearchParams,
              });
            }}
          />
        </div>
      </Card>
      <NewCaseModal
        DesignSystemComponents={DesignSystemComponents}
        show={showCaseModal}
        onClose={() => {
          setShowCaseModal(false);
          setFromMonitoring(false);
          setMonitoringId(null);
          setMonitoringIdentifier(null);
          setPreselectedClient(null);
          setPreselectedRol(null);
          setClients([]);
        }}
        clients={clients}
        lawyers={lawyers}
        courts={courtOptions}
        createInfo={createInfo}
        userId={userId}
        sponsors={sponsors}
        onSearchLawyers={fetchUsers}
        onSearchClients={fetchClients}
        fromMonitoring={fromMonitoring}
        monitoringId={monitoringId}
        preselectedClient={preselectedClient}
        preselectedRol={preselectedRol}
        onAfterCreate={() => {
          setFromMonitoring(false);
          setMonitoringId(null);
          setPreselectedClient(null);
        }}
      />
    </div>
  );
};

export default LegalCases;
